import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { gradYearFromGrade } from '@/lib/gradYear';
import type { Player, BallArsenalItem, FAQItem } from '@/types';
import {
  User,
  Camera,
  Save,
  Search,
  TrendingUp,
  Trophy,
  BarChart3,
  Plus,
  Trash2,
  ExternalLink,
  CheckCircle2,
  Clock,
  CircleDashed,
} from 'lucide-react';
import { BallPicker, type BallPickerSelection } from '@/components/BallPicker';

const BALL_ROLES: BallArsenalItem['role'][] = ['benchmark', 'heavy-oil', 'medium', 'burn', 'spare', 'situational'];

const emptyBall = (pos: number): BallArsenalItem => ({
  name: '',
  brand: '',
  coverstock: '',
  core: '',
  surface: '',
  bag: 1,
  bagPosition: pos,
  role: 'benchmark',
  notes: '',
  imageUrl: '',
  ballDbId: '',
});

const inputCls =
  'w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition';
const smallInputCls =
  'w-full px-3 py-2 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition text-sm';
const labelCls = 'block text-sm font-bold text-tiger-neutral-700 mb-1';
const smallLabelCls = 'text-xs font-bold text-tiger-neutral-500 block mb-1';

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <section className="bg-white rounded-2xl shadow-tiger p-6">
    <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">{title}</h2>
    {hint && <p className="text-sm text-tiger-neutral-500 mt-1">{hint}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

export const PlayerDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [player, setPlayer] = useState<Player | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Basics
  const [name, setName] = useState('');
  const [grade, setGrade] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [hand, setHand] = useState('');
  const [favoriteBallBrand, setFavoriteBallBrand] = useState('');
  const [usbcId, setUsbcId] = useState('');
  const [bio, setBio] = useState('');

  // Academics & goals
  const [gpa, setGpa] = useState('');
  const [actSat, setActSat] = useState('');
  const [intendedMajor, setIntendedMajor] = useState('');
  const [goals, setGoals] = useState('');

  // My bag + Q&A
  const [arsenal, setArsenal] = useState<BallArsenalItem[]>([]);
  const [faq, setFaq] = useState<FAQItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const resolvePlayerDoc = async () => {
      if (!currentUser) return;

      try {
        // Primary: player doc claimed by sign-in email
        let resolvedId: string | null = null;
        let data: Record<string, unknown> | null = null;

        if (currentUser.email) {
          const q = query(collection(db, 'players'), where('email', '==', currentUser.email), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            resolvedId = snap.docs[0].id;
            data = snap.docs[0].data();
          }
        }

        // Legacy fallback: doc keyed by auth uid
        if (!resolvedId) {
          const legacy = await getDoc(doc(db, 'players', currentUser.uid));
          if (legacy.exists()) {
            resolvedId = legacy.id;
            data = legacy.data();
          }
        }

        if (!resolvedId || !data) {
          setNotLinked(true);
          return;
        }

        const p = { id: resolvedId, ...data } as Player;
        setDocId(resolvedId);
        setPlayer(p);

        setName(p.name || '');
        setGrade(p.grade || '');
        setGraduationYear(p.graduationYear ? String(p.graduationYear) : '');
        setHand(p.hand || '');
        setFavoriteBallBrand((p as unknown as { favoriteBallBrand?: string }).favoriteBallBrand || '');
        // USBC ID: from the player doc, else prefilled from what they entered at signup
        // (stored on their users doc) so their first Save writes it onto the player doc.
        if (p.usbcId) {
          setUsbcId(p.usbcId);
        } else if (currentUser?.uid) {
          const uSnap = await getDoc(doc(db, 'users', currentUser.uid));
          setUsbcId((uSnap.exists() ? (uSnap.data() as { usbcId?: string }).usbcId : '') || '');
        }
        setBio(p.bio || '');
        setGpa(p.academics?.gpa !== undefined ? String(p.academics.gpa) : '');
        setActSat(p.academics?.actSat || '');
        setIntendedMajor(p.academics?.intendedMajor || '');
        setGoals(p.goals || '');
        setArsenal(p.ballArsenal || []);
        setFaq(p.faq || []);
      } catch (error) {
        console.error('Error resolving player profile:', error);
        setErrorMessage('Failed to load your profile. Try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    resolvePlayerDoc();
  }, [currentUser]);

  const setBall = (i: number, patch: Partial<BallArsenalItem>) =>
    setArsenal((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const setFaqItem = (i: number, patch: Partial<FAQItem>) =>
    setFaq((prev) => prev.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const handlePickBall = (ball: BallPickerSelection) =>
    setArsenal((prev) => [
      ...prev,
      {
        name: ball.name,
        brand: ball.brand,
        coverstock: ball.coverstock,
        core: ball.core,
        surface: '',
        bag: 1,
        bagPosition: prev.length + 1,
        role: 'benchmark',
        notes: '',
        imageUrl: ball.imageUrl,
        ballDbId: ball.ballDbId,
      },
    ]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser || !docId) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Photo must be less than 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload an image file');
      return;
    }

    setUploadingPhoto(true);
    setErrorMessage('');

    try {
      const storageRef = ref(storage, `player-profiles/${currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'players', docId), {
        photoURL,
        updatedAt: serverTimestamp(),
      });

      setPlayer((prev) => (prev ? { ...prev, photoURL } : prev));
      setSuccessMessage('Photo uploaded!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setErrorMessage('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleSaveAll = async () => {
    if (!docId) return;

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const academics: Record<string, unknown> = {};
      if (gpa.trim() !== '' && !Number.isNaN(parseFloat(gpa))) academics.gpa = parseFloat(gpa);
      if (actSat.trim()) academics.actSat = actSat.trim();
      if (intendedMajor.trim()) academics.intendedMajor = intendedMajor.trim();

      // Player-editable fields only (usbcId now allowed — required so the scrape
      // can find their leagues; coach still verifies before publish). Never write
      // email, published, consentOnFile, or stats fields — rules reject the update.
      const parsedGradYear = parseInt(graduationYear, 10);
      const updates = {
        name: name.trim(),
        grade,
        graduationYear: Number.isNaN(parsedGradYear) ? null : parsedGradYear,
        hand: hand || null,
        favoriteBallBrand: favoriteBallBrand.trim(),
        bio: bio.trim(),
        academics: Object.keys(academics).length ? academics : null,
        goals: goals.trim(),
        ballArsenal: arsenal.filter((b) => b.name.trim()),
        faq: faq.filter((f) => f.question.trim() && f.answer.trim()),
        usbcId: usbcId.trim() || null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'players', docId), updates);

      setSuccessMessage('Profile saved!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrorMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tiger-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiger-primary-black"></div>
      </div>
    );
  }

  if (notLinked || !player || !docId) {
    return (
      <div className="min-h-screen bg-tiger-neutral-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-tiger p-8 max-w-md text-center">
          <User className="w-12 h-12 mx-auto text-tiger-neutral-400 mb-4" />
          <h1 className="font-heading text-2xl text-tiger-primary-black uppercase tracking-wide mb-2">
            Profile not linked
          </h1>
          <p className="text-tiger-neutral-600">
            You're in! Your account{currentUser?.email ? ` (${currentUser.email})` : ''} is waiting
            for Coach to connect it to your player card, new signups show up on the coach's
            onboarding list automatically. Check back soon, and tell Coach if it takes more than a
            day or two.
          </p>
        </div>
      </div>
    );
  }

  const published = !!player.published && !!player.consentOnFile;
  const statusChip = published ? (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
      <CheckCircle2 className="w-3.5 h-3.5" /> Published
    </span>
  ) : !player.consentOnFile ? (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
      <Clock className="w-3.5 h-3.5" /> Awaiting consent form
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-tiger-neutral-200 text-tiger-neutral-700">
      <CircleDashed className="w-3.5 h-3.5" /> Not published yet
    </span>
  );

  return (
    <div className="min-h-screen bg-tiger-neutral-50">
      {/* Header */}
      <section className="bg-tiger-primary-black text-white py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-black mb-2">
                {player.name || 'Player Dashboard'}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                {statusChip}
                <Link
                  to={`/player/${docId}`}
                  className="inline-flex items-center gap-1.5 text-sm text-tiger-neutral-300 hover:text-white underline underline-offset-2"
                >
                  View my public profile <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Messages */}
        {successMessage && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{errorMessage}</div>
        )}

        {/* Read-only stats summary */}
        {player.statsSnapshot && (
          <section className="bg-white rounded-2xl shadow-tiger p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">My Stats</h2>
              <span className="text-xs text-tiger-neutral-500">synced from team stats</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-tiger-neutral-50 rounded-lg p-4 border border-tiger-neutral-200 text-center">
                <TrendingUp className="w-5 h-5 mx-auto mb-1 text-tiger-tiger-darkRed" />
                <div className="text-2xl font-display font-black text-tiger-primary-black">
                  {player.statsSnapshot.average}
                </div>
                <div className="text-xs font-semibold text-tiger-neutral-600">Average</div>
              </div>
              <div className="bg-tiger-neutral-50 rounded-lg p-4 border border-tiger-neutral-200 text-center">
                <Trophy className="w-5 h-5 mx-auto mb-1 text-tiger-tiger-gold" />
                <div className="text-2xl font-display font-black text-tiger-primary-black">
                  {player.statsSnapshot.highGame}
                </div>
                <div className="text-xs font-semibold text-tiger-neutral-600">High Game</div>
              </div>
              <div className="bg-tiger-neutral-50 rounded-lg p-4 border border-tiger-neutral-200 text-center">
                <BarChart3 className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                <div className="text-2xl font-display font-black text-tiger-primary-black">
                  {player.statsSnapshot.games}
                </div>
                <div className="text-xs font-semibold text-tiger-neutral-600">Games</div>
              </div>
            </div>
          </section>
        )}

        {/* Photo */}
        <Section title="Profile Photo">
          <div className="flex items-center gap-6">
            <div className="relative inline-block shrink-0">
              <div className="w-24 h-24 rounded-full bg-tiger-primary-black flex items-center justify-center overflow-hidden">
                {player.photoURL ? (
                  <img src={player.photoURL} alt={player.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-white" />
                )}
              </div>
              <label
                htmlFor="photo-upload"
                className="absolute bottom-0 right-0 bg-tiger-tiger-gold text-tiger-primary-black p-2 rounded-full cursor-pointer hover:bg-opacity-90 transition-all shadow-tiger"
              >
                <Camera className="w-4 h-4" />
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={uploadingPhoto}
              />
            </div>
            <p className="text-sm text-tiger-neutral-500">
              {uploadingPhoto ? 'Uploading…' : 'Tap the camera to upload a new photo. Images only, 5MB max.'}
            </p>
          </div>
        </Section>

        {/* Basics */}
        <Section title="Basics">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="usbcId" className={labelCls}>
                USBC Member ID <span className="text-red-600">*</span>
              </label>
              <input
                id="usbcId"
                className={`${inputCls} ${!usbcId.trim() ? 'border-red-400 bg-red-50' : ''}`}
                value={usbcId}
                onChange={(e) => setUsbcId(e.target.value)}
                placeholder="23-9833"
              />
              <p className="text-xs text-tiger-neutral-500 mt-1">
                {usbcId.trim()
                  ? 'Links your sanctioned leagues. Your coach verifies it before your profile goes public.'
                  : 'Required, enter your USBC Member ID so we can pull your league stats, then hit Save.'}
              </p>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="name" className={labelCls}>Name</label>
              <input id="name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label htmlFor="grade" className={labelCls}>Grade</label>
              <select
                id="grade"
                className={inputCls}
                value={grade}
                onChange={(e) => {
                  setGrade(e.target.value);
                  const gy = gradYearFromGrade(e.target.value);
                  if (gy) setGraduationYear(String(gy)); // auto-fill grad year (editable)
                }}
              >
                <option value="">Select grade</option>
                <option value="9">9th Grade</option>
                <option value="10">10th Grade</option>
                <option value="11">11th Grade</option>
                <option value="12">12th Grade</option>
              </select>
            </div>
            <div>
              <label htmlFor="graduationYear" className={labelCls}>Graduation Year</label>
              <input
                id="graduationYear"
                type="number"
                className={inputCls}
                value={graduationYear}
                onChange={(e) => setGraduationYear(e.target.value)}
                placeholder="2028"
              />
            </div>
            <div>
              <label htmlFor="hand" className={labelCls}>Hand</label>
              <select id="hand" className={inputCls} value={hand} onChange={(e) => setHand(e.target.value)}>
                <option value="">Select</option>
                <option value="right">Right</option>
                <option value="left">Left</option>
                <option value="two-handed">Two-handed</option>
              </select>
            </div>
            <div>
              <label htmlFor="favoriteBallBrand" className={labelCls}>Favorite Ball Brand</label>
              <input
                id="favoriteBallBrand"
                className={inputCls}
                value={favoriteBallBrand}
                onChange={(e) => setFavoriteBallBrand(e.target.value)}
                placeholder="Storm, Brunswick, Motiv…"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="bio" className={labelCls}>Bio</label>
              <textarea
                id="bio"
                className={`${inputCls} resize-none`}
                rows={4}
                maxLength={500}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself, your bowling journey, or your goals…"
              />
              <p className="mt-1 text-sm text-tiger-neutral-500">{bio.length}/500 characters</p>
            </div>
          </div>
        </Section>

        {/* Academics & goals */}
        <Section title="Academics & Goals" hint="College coaches look at grades first. Keep this current.">
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="gpa" className={labelCls}>GPA</label>
              <input
                id="gpa"
                type="number"
                step="0.01"
                min="0"
                max="5"
                className={inputCls}
                value={gpa}
                onChange={(e) => setGpa(e.target.value)}
                placeholder="3.75"
              />
            </div>
            <div>
              <label htmlFor="actSat" className={labelCls}>ACT / SAT</label>
              <input
                id="actSat"
                className={inputCls}
                value={actSat}
                onChange={(e) => setActSat(e.target.value)}
                placeholder="ACT 28"
              />
            </div>
            <div>
              <label htmlFor="intendedMajor" className={labelCls}>Intended Major</label>
              <input
                id="intendedMajor"
                className={inputCls}
                value={intendedMajor}
                onChange={(e) => setIntendedMajor(e.target.value)}
                placeholder="Engineering"
              />
            </div>
          </div>
          <div>
            <label htmlFor="goals" className={labelCls}>Goals</label>
            <textarea
              id="goals"
              className={`${inputCls} resize-none`}
              rows={3}
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              placeholder="College bowling goals, dream schools, what you're working toward…"
            />
          </div>
        </Section>

        {/* My bag */}
        <Section title="My Bag" hint="Bag position 1 = first ball out of the bag. Rows without a ball name are dropped on save.">
          <div className="space-y-4">
            {arsenal.map((b, i) => (
              <div key={i} className="border border-tiger-neutral-200 rounded-xl p-4 flex gap-3 relative">
                {b.imageUrl && (
                  <img
                    src={b.imageUrl}
                    alt={`${b.brand} ${b.name}`.trim()}
                    className="w-12 h-12 object-contain bg-tiger-neutral-50 rounded shrink-0"
                  />
                )}
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className={smallLabelCls}>Ball name</label>
                  <input className={smallInputCls} value={b.name} onChange={(e) => setBall(i, { name: e.target.value })} placeholder="Phaze II" />
                </div>
                <div>
                  <label className={smallLabelCls}>Brand</label>
                  <input className={smallInputCls} value={b.brand} onChange={(e) => setBall(i, { brand: e.target.value })} placeholder="Storm" />
                </div>
                <div>
                  <label className={smallLabelCls}>Coverstock</label>
                  <input className={smallInputCls} value={b.coverstock} onChange={(e) => setBall(i, { coverstock: e.target.value })} placeholder="R2S Solid" />
                </div>
                <div>
                  <label className={smallLabelCls}>Core</label>
                  <input className={smallInputCls} value={b.core || ''} onChange={(e) => setBall(i, { core: e.target.value })} placeholder="Velocity" />
                </div>
                <div>
                  <label className={smallLabelCls}>Surface</label>
                  <input className={smallInputCls} value={b.surface || ''} onChange={(e) => setBall(i, { surface: e.target.value })} placeholder="3000 grit" />
                </div>
                <div>
                  <label className={smallLabelCls}>Bag #</label>
                  <input
                    className={smallInputCls}
                    type="number"
                    min={1}
                    value={b.bag ?? 1}
                    onChange={(e) => setBall(i, { bag: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <label className={smallLabelCls}>Bag position</label>
                  <input
                    className={smallInputCls}
                    type="number"
                    min={1}
                    value={b.bagPosition}
                    onChange={(e) => setBall(i, { bagPosition: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <label className={smallLabelCls}>Role</label>
                  <select className={smallInputCls} value={b.role} onChange={(e) => setBall(i, { role: e.target.value as BallArsenalItem['role'] })}>
                    {BALL_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={smallLabelCls}>Notes</label>
                  <input className={smallInputCls} value={b.notes || ''} onChange={(e) => setBall(i, { notes: e.target.value })} placeholder="Fresh oil look" />
                </div>
                </div>
                <button
                  type="button"
                  onClick={() => setArsenal((prev) => prev.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 bg-white border border-tiger-neutral-300 rounded-full p-1.5 text-tiger-neutral-500 hover:text-red-600 hover:border-red-300 shadow-tiger"
                  title="Remove ball"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setArsenal((prev) => [...prev, emptyBall(prev.length + 1)])}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
              >
                <Plus className="w-4 h-4" /> Add ball
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
              >
                <Search className="w-4 h-4" /> Search ball database
              </button>
            </div>
          </div>
        </Section>

        {/* Q&A */}
        <Section title="Q&A" hint="Interview-style questions and your answers. Incomplete rows are dropped on save.">
          <div className="space-y-4">
            {faq.map((f, i) => (
              <div key={i} className="border border-tiger-neutral-200 rounded-xl p-4 space-y-3 relative">
                <div>
                  <label className={smallLabelCls}>Question</label>
                  <input
                    className={smallInputCls}
                    value={f.question}
                    onChange={(e) => setFaqItem(i, { question: e.target.value })}
                    placeholder="What got you into bowling?"
                  />
                </div>
                <div>
                  <label className={smallLabelCls}>Answer</label>
                  <textarea
                    className={`${smallInputCls} resize-none`}
                    rows={3}
                    value={f.answer}
                    onChange={(e) => setFaqItem(i, { answer: e.target.value })}
                    placeholder="Your answer…"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setFaq((prev) => prev.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 bg-white border border-tiger-neutral-300 rounded-full p-1.5 text-tiger-neutral-500 hover:text-red-600 hover:border-red-300 shadow-tiger"
                  title="Remove question"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setFaq((prev) => [...prev, { question: '', answer: '' }])}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Add question
            </button>
          </div>
        </Section>

        {/* Save All */}
        <div className="sticky bottom-4">
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={saving}
            className="w-full bg-tiger-primary-black text-white py-4 rounded-lg font-bold hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-tiger"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Saving…
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save All
              </>
            )}
          </button>
        </div>
      </div>

      <BallPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handlePickBall} />
    </div>
  );
};
